import { LegalDocument } from '@westhetics/legal/react'
import {
  ActionError,
  Button,
  ConfirmDialog,
  EmptyState,
  ListRow,
  ListRows,
  Skeleton,
  StatusTag,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
  formatCurrency,
  formatDate,
  formatRelative,
  titleCase,
  toast,
} from '@westhetics/ui'
import { useState } from 'react'
import { Link, useMatch, useNavigate } from 'react-router'
import { ListFooter } from '@/app/list-footer'
import { LoadError } from '@/app/load-error'
import { formatDateTime } from '@/features/anamneses/labels'
import type { Patient } from '@/features/patients/api'
import { isApiError } from '@/lib/api'
import { errorMessage } from '@/lib/errors'
import { routes } from '@/routes'
import {
  openSignedUrl,
  useDiscardAppointment,
  useDocumentDetail,
  usePatientDocuments,
  usePdfLink,
  type DocumentDraft,
  type PatientAppointment,
  type PatientDocumentSummary,
} from './api'
import { ADDITION, FLOW_TITLES, RESTRICTED_NOTE, additionOf, additionTitle } from './labels'
import { personWords } from '@/features/patients/labels'
import { deliverySummaryOf as deliverySummary } from '@/features/signatures/labels'

/**
 * A aba Documentos da ficha (docs/areas/pacientes.md, "Abas"; `GET /patients/:id/documents`). Um
 * bloco por atendimento gerado, com os documentos dele (DOCUMENTO · ENTREGA · STATUS) e os rascunhos
 * do fluxo de geração ("Rascunho de documentos · começado há 2 dias · Retomar · Descartar",
 * RN-DOC-35). Clicar num documento abre a leitura logo abaixo, na mesma tela, com o PDF para baixar
 * (RN-DOC-29); o de anamnese só para quem a lê (RN-DOC-34). O atendimento já enviado oferece "Adicionar documentos"
 * (RN-DOC-57); os adicionados aparecem num bloco próprio, com o envio deles e a data do atendimento original.
 */

export function PatientDocumentsTab({ patient }: { patient: Patient }) {
  const navigate = useNavigate()
  const match = useMatch(routes.patientDocument(':patientId', ':appointmentId', ':documentId'))
  const open = match?.params.patientId === patient.id ? match.params : null
  if (open?.appointmentId && open.documentId) {
    return (
      <DocumentReading
        key={open.documentId}
        appointmentId={open.appointmentId}
        documentId={open.documentId}
        onBack={() => navigate(routes.patientTab(patient.id, 'documents'))}
      />
    )
  }
  return <PatientDocumentsList key={patient.id} patient={patient} />
}

function PatientDocumentsList({ patient }: { patient: Patient }) {
  const [page, setPage] = useState(1)
  const query = usePatientDocuments(patient.id, page)
  const canEdit = patient.status === 'active' && !patient.isReadOnly
  const needsRecord = canEdit && !patient.completeness.isReadyForContract
  // Quem não é paciente só tem documentos avulsos (RN-PAC-41).
  const isPatient = patient.audience === 'patient'
  const words = personWords(patient.audience)
  const items = query.data?.data ?? []
  const drafts = query.data?.meta.drafts ?? []

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="eyebrow">
          {isPatient ? 'Documentos desta paciente' : `Documentos deste ${words.one}`}
          {query.data ? ` · ${query.data.meta.total}` : ''}
        </h2>
        {canEdit && items.length > 0 ? (
          <Button variant="outline" size="sm" asChild>
            <Link to={routes.standaloneDocuments(patient.id)}>Documentos avulsos</Link>
          </Button>
        ) : null}
      </div>

      {drafts.map((draft) => (
        <DraftRow key={draft.appointmentId} patientId={patient.id} draft={draft} />
      ))}

      {query.isError ? (
        <LoadError
          title="Não conseguimos carregar os documentos desta paciente"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : query.isPending ? (
        <TableSkeleton rows={3} columns={4} />
      ) : items.length === 0 ? (
        <EmptyState
          title="Nenhum documento ainda"
          description={
            isPatient
              ? 'Contratos e termos gerados para esta paciente aparecem aqui, com o status da assinatura e a entrega por canal. É o que prova que ela leu e assinou antes do procedimento.'
              : `Contratos e termos gerados para este ${words.one} aparecem aqui, com o status da assinatura e a entrega por canal. É o que prova o que foi combinado.`
          }
          action={
            needsRecord ? (
              <Button variant="outline" asChild>
                <Link to={routes.patientTab(patient.id, 'details')}>Completar cadastro</Link>
              </Button>
            ) : canEdit ? (
              <Button variant="outline" asChild>
                {isPatient ? (
                  <Link to={routes.generateDocuments(patient.id)}>
                    Gerar documento com anamnese
                  </Link>
                ) : (
                  <Link to={routes.standaloneDocuments(patient.id)}>Documentos avulsos</Link>
                )}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="flex flex-col gap-6">
          {items.map((appointment) => (
            <AppointmentBlock
              key={appointment.appointmentId}
              patientId={patient.id}
              appointment={appointment}
              canEdit={canEdit}
            />
          ))}
          {query.data ? (
            <ListFooter
              meta={query.data.meta}
              noun={['atendimento', 'atendimentos']}
              note="clique num documento para ler e baixar o PDF"
              onPage={setPage}
            />
          ) : null}
        </div>
      )}
    </div>
  )
}

/** "Rascunho de documentos · começado há 2 dias · Retomar · Descartar" (RN-DOC-35). */
function DraftRow({ patientId, draft }: { patientId: string; draft: DocumentDraft }) {
  const discard = useDiscardAppointment()
  const [asking, setAsking] = useState(false)
  const resume =
    draft.type === 'procedure'
      ? routes.generateDocuments(patientId, draft.appointmentId)
      : routes.standaloneDocuments(patientId, draft.appointmentId)
  return (
    <>
      <ListRows>
        <ListRow
          size="lead"
          title={
            draft.parentAppointmentId
              ? ADDITION.draftTitle
              : `Rascunho de ${draft.type === 'procedure' ? 'documentos' : 'documentos avulsos'}`
          }
          meta={`começado ${formatRelative(new Date(draft.createdAt))}${draft.createdBy ? ` por ${titleCase(draft.createdBy.name)}` : ''} · etapa ${draft.step} de 3`}
          trailing={
            <div className="flex items-center gap-3">
              <Button variant="text" size="sm" asChild>
                <Link to={resume}>Retomar</Link>
              </Button>
              <Button variant="text" size="sm" onClick={() => setAsking(true)}>
                Descartar
              </Button>
            </div>
          }
        />
      </ListRows>
      <ConfirmDialog
        open={asking}
        onOpenChange={setAsking}
        tone="primary"
        title="Descartar o rascunho?"
        effect="O rascunho some da ficha. O cadastro, as anamneses e os documentos já gerados não mudam."
        confirmLabel="Descartar rascunho"
        loading={discard.isPending}
        loadingLabel="Descartando…"
        onConfirm={() =>
          discard.mutate(draft.appointmentId, {
            onSuccess: () => {
              setAsking(false)
              toast('Rascunho descartado.')
            },
            onError: (error) => {
              setAsking(false)
              toast(errorMessage(error))
            },
          })
        }
      />
    </>
  )
}

/** Um atendimento gerado e os documentos dele, na ordem RN-DOC-27. */
function AppointmentBlock({
  patientId,
  appointment,
  canEdit,
}: {
  patientId: string
  appointment: PatientAppointment
  canEdit: boolean
}) {
  const navigate = useNavigate()
  const pdf = usePdfLink()
  const [busyId, setBusyId] = useState<string | null>(null)
  const parent = appointment.parentAppointment
  const title = parent
    ? additionTitle(formatDateTime(appointment.generatedAt))
    : [
        FLOW_TITLES[appointment.type] === FLOW_TITLES.procedure
          ? 'Atendimento'
          : 'Documentos avulsos',
        formatDateTime(appointment.generatedAt),
      ].join(' · ')
  // Depois do envio, outro termo para o mesmo atendimento vai num envio próprio (RN-DOC-57).
  const canAdd =
    canEdit && !parent && appointment.type === 'procedure' && appointment.state === 'sent'
  const meta = [
    parent ? additionOf(formatDate(new Date(parent.generatedAt))) : null,
    appointment.procedures.length > 0 ? appointment.procedures.join(', ') : null,
    appointment.practitioner ? titleCase(appointment.practitioner.name) : null,
    appointment.totalAmountCents !== null
      ? formatCurrency(appointment.totalAmountCents / 100)
      : null,
    appointment.generation > 1 ? `geração ${appointment.generation}` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  function download(doc: PatientDocumentSummary) {
    setBusyId(doc.id)
    pdf.mutate(
      { appointmentId: appointment.appointmentId, documentId: doc.id },
      {
        onSuccess: (link) => openSignedUrl(link.url),
        onError: (error) => toast(errorMessage(error)),
        onSettled: () => setBusyId(null),
      },
    )
  }

  return (
    <section aria-label={title}>
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <div className="font-display text-ink text-[14px] leading-[1.3]">{title}</div>
          {meta ? (
            <div className="font-body text-secondary mt-1 text-[12px] leading-[1.5]">{meta}</div>
          ) : null}
        </div>
        <div className="flex items-center gap-3">
          <StatusTag level={appointment.level}>{appointment.label}</StatusTag>
          {appointment.signatureRequest ? (
            <Button variant="text" size="sm" asChild>
              <Link to={routes.signature(appointment.appointmentId)}>Ver envio</Link>
            </Button>
          ) : null}
          {canAdd ? (
            <Button variant="text" size="sm" asChild>
              <Link to={routes.addDocuments(patientId, appointment.appointmentId)}>
                {ADDITION.action}
              </Link>
            </Button>
          ) : null}
          {appointment.state === 'generated' ? (
            <Button variant="text" size="sm" asChild>
              <Link
                to={
                  appointment.type === 'procedure'
                    ? routes.generateDocuments(patientId, appointment.appointmentId)
                    : routes.standaloneDocuments(patientId, appointment.appointmentId)
                }
              >
                Abrir atendimento
              </Link>
            </Button>
          ) : null}
        </div>
      </div>
      <Table>
        <TableHeader>
          <tr>
            <TableHead>Documento</TableHead>
            <TableHead>Entrega</TableHead>
            <TableHead>Status</TableHead>
            <TableHead aria-label="Ações" />
          </tr>
        </TableHeader>
        <TableBody>
          {appointment.documents.map((doc) => (
            <TableRow
              key={doc.id}
              critical={doc.level === 'critical'}
              onClick={
                doc.isRestricted
                  ? undefined
                  : () =>
                      navigate(routes.patientDocument(patientId, appointment.appointmentId, doc.id))
              }
            >
              <TableCell label="Documento">
                {doc.name}
                {doc.isRestricted ? (
                  <div className="font-body text-secondary mt-1 text-[12px] leading-[1.5]">
                    {RESTRICTED_NOTE}
                  </div>
                ) : null}
              </TableCell>
              <TableCell label="Entrega" muted>
                {appointment.signatureRequest
                  ? deliverySummary(appointment.signatureRequest.delivery)
                  : 'Não enviado'}
              </TableCell>
              <TableCell label="Status">
                <StatusTag level={doc.level}>{doc.label}</StatusTag>
              </TableCell>
              <TableCell>
                {doc.isRestricted ? null : (
                  <Button
                    variant="text"
                    size="sm"
                    loading={busyId === doc.id}
                    loadingLabel="Preparando…"
                    onClick={(e) => {
                      e.stopPropagation()
                      download(doc)
                    }}
                  >
                    PDF
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </section>
  )
}

/** O documento aberto dentro da aba: o Markdown resolvido com `<LegalDocument>` e o PDF para baixar. */
function DocumentReading({
  appointmentId,
  documentId,
  onBack,
}: {
  appointmentId: string
  documentId: string
  onBack: () => void
}) {
  const query = useDocumentDetail(appointmentId, documentId)
  const pdf = usePdfLink()
  const back = (
    <Button variant="outline" onClick={onBack}>
      Voltar à lista
    </Button>
  )

  if (query.isPending) {
    return (
      <div role="status" aria-label="Carregando o documento" className="flex flex-col gap-4">
        <Skeleton className="h-[18px] w-[260px]" />
        <Skeleton className="h-[320px] w-full" />
      </div>
    )
  }
  if (query.isError) {
    const error = query.error
    if (isApiError(error) && error.code === 'forbidden') {
      return (
        <EmptyState
          title="Leitura disponível para a profissional responsável e a gestora"
          description={errorMessage(error)}
          action={back}
        />
      )
    }
    return (
      <LoadError
        title="Não conseguimos abrir este documento"
        error={error}
        onRetry={() => void query.refetch()}
      />
    )
  }

  const doc = query.data
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-display text-ink text-lead leading-[1.3]">{doc.name}</div>
          <div className="font-body text-secondary mt-1 text-[12px] leading-[1.5]">
            {[
              `versão ${doc.version ?? '—'}`,
              `gerado em ${formatDateTime(doc.generatedAt)}`,
              doc.shortHash ? `hash ${doc.shortHash}` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <StatusTag level={doc.level}>{doc.label}</StatusTag>
          <Button
            variant="outline"
            size="sm"
            loading={pdf.isPending}
            loadingLabel="Preparando…"
            onClick={() =>
              pdf.mutate(
                { appointmentId, documentId },
                {
                  onSuccess: (link) => openSignedUrl(link.url),
                  onError: (error) => toast(errorMessage(error)),
                },
              )
            }
          >
            Baixar PDF
          </Button>
          {back}
        </div>
      </div>
      {pdf.isError ? (
        <ActionError
          title="Não foi possível preparar o PDF"
          description={errorMessage(pdf.error)}
        />
      ) : null}
      <div className="border-hairline bg-surface max-desk:px-4 border px-6 py-6">
        <LegalDocument markdown={doc.resolvedMarkdown ?? ''} />
      </div>
    </div>
  )
}
